import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getUserAccessInfo } from "@/integrations/firebase/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Briefcase, FileText, Calendar, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PendingUserApprovals } from "@/components/PendingUserApprovals";
import { PageHeader } from "@/components/PageHeader";

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isCrewLeader, setIsCrewLeader] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const hasHandledStripeSession = useRef(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          // Don't redirect if we have a session_id - user just completed payment
          const sessionId = searchParams.get('session_id');
          if (!sessionId) {
            navigate("/auth");
          } else {
            // User just paid but auth not loaded yet - wait for auth
            console.log('Waiting for auth to load after payment...');
          }
          return;
        }
        
        // Get user document first to check account mode
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        let mode = "";
        let userData: any = null;
        
        if (userDoc.exists()) {
          userData = userDoc.data();
          mode = userData?.accountMode || userData?.account_mode || "";
          
          // If subscription is active, ensure the flag is set and clear temporary flags
          if (userData?.subscription?.status === 'active') {
            sessionStorage.setItem('subscription_active', 'true');
            sessionStorage.removeItem('just_completed_payment');
          }
        } else {
          console.error('User document does not exist for:', currentUser.uid);
          // User document doesn't exist - this shouldn't happen after signup
          toast({
            title: "Account Setup Error",
            description: "Your account data is missing. Please contact support.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        
        // Get comprehensive user access info (handles Personal accounts automatically)
        const accessInfo = await getUserAccessInfo(currentUser.uid);
        
        // Check if user has a client document (they're a client)
        const clientDoc = await getDoc(doc(db, "clients", currentUser.uid));
        if (clientDoc.exists()) {
          navigate("/client-portal");
          return;
        }
        
        // If user ONLY has client role, redirect to client portal
        if (accessInfo.roles.includes("client") && !accessInfo.isAdmin && !accessInfo.isManager && !accessInfo.isCrewLeader && !accessInfo.isEmployee) {
          navigate("/client-portal");
          return;
        }
        
        // Personal accounts have full access (even with empty roles array)
        // Business accounts need at least one contractor role
        const hasContractorRole = accessInfo.roles.length > 0 && accessInfo.roles.some(r => r !== 'client');
        
        if (!hasContractorRole && !accessInfo.isPersonalAccount) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access this page",
            variant: "destructive",
          });
          navigate("/auth");
          return;
        }
        
        setUser(currentUser);

        if (userDoc.exists()) {
          setProfile(userData);
        }

        setIsAdmin(accessInfo.isAdmin);
        setIsManager(accessInfo.isManager);
        setIsCrewLeader(accessInfo.isCrewLeader);
        setIsEmployee(accessInfo.isEmployee);
        setLoading(false);

        // Redirect to Permit Ops Center
        navigate("/permits", { replace: true });
      } catch (error) {
        console.error('Error in checkAuth:', error);
        setLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        checkAuth();
      } else {
        // Check if we have session_id before redirecting
        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
          navigate("/auth");
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, toast, searchParams]);

  // Handle Stripe checkout success - poll for subscription activation
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId && !hasHandledStripeSession.current && user) {
      hasHandledStripeSession.current = true;
      
      toast({
        title: "Payment Successful!",
        description: "Activating your subscription...",
      });
      
      // Poll for subscription activation
      let attempts = 0;
      const maxAttempts = 20; // 20 seconds max
      
      const pollSubscription = setInterval(async () => {
        attempts++;
        
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check if subscription is now active
            if (userData?.subscription?.status === 'active') {
              clearInterval(pollSubscription);
              
              // Mark in sessionStorage that subscription is confirmed active
              sessionStorage.setItem('subscription_active', 'true');
              
              // Clear URL and refresh to load with active subscription
              setSearchParams({});
              window.location.reload();
              return;
            }
          }
        } catch (error) {
          console.error('Error polling subscription:', error);
        }
        
        // If max attempts reached, refresh anyway
        if (attempts >= maxAttempts) {
          clearInterval(pollSubscription);
          setSearchParams({});
          window.location.reload();
        }
      }, 1000); // Poll every second
      
      return () => clearInterval(pollSubscription);
    }
  }, [searchParams, toast, user, setSearchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tradeLabels: Record<string, string> = {
    electrician: "Electrician",
    gas: "Natural/Propane Gas",
    plumber: "Plumber",
    hvac: "HVAC",
    other: "Other",
  };

  const displayTradeType = profile?.trade_type === "other" && profile?.custom_trade_type
    ? profile.custom_trade_type
    : tradeLabels[profile?.trade_type || ""] || profile?.trade_type;

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Overview" />
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Pending Approvals for Admins */}
        {isAdmin && <PendingUserApprovals />}

        {/* Welcome Section */}
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {getGreeting()}, {profile?.fullName || profile?.full_name || "there"}!
          </h2>
          <p className="text-muted-foreground mt-1">
            Here's an overview of your business today.
          </p>
        </div>

        {/* Stats Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Jobs</CardTitle>
            <Briefcase className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">--</div>
            <p className="text-xs text-muted-foreground">
              View in Jobs section
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Estimates</CardTitle>
            <FileText className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">--</div>
            <p className="text-xs text-muted-foreground">
              Awaiting approval
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">This Week's Hours</CardTitle>
            <Clock className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">--</div>
            <p className="text-xs text-muted-foreground">
              Tracked time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Revenue (MTD)</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">--</div>
            <p className="text-xs text-muted-foreground">
              View in Analytics
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate("/jobs")}
            >
              <Briefcase className="h-4 w-4 mr-2" />
              Create New Job
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate("/estimates")}
            >
              <FileText className="h-4 w-4 mr-2" />
              Create Estimate
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate("/time")}
            >
              <Clock className="h-4 w-4 mr-2" />
              Clock In/Out
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate("/schedule")}
            >
              <Calendar className="h-4 w-4 mr-2" />
              View Schedule
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity / Upcoming */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No appointments scheduled for today.</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => navigate("/schedule")}
              >
                View full schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
};

export default Dashboard;
